// ゲーム内アンケート（お知らせの先頭の札から開く）。設問は運営APIで差し替わるデータ（packages/core の SurveyDef）。
// 選択はチップ、必須が埋まるまで送れない。最初に答えたときだけお礼が入る（答え直しは上書き）。
import { multiMax, textMax, type SurveyAnswers, type SurveyDef } from '@hyakki/core';
import { api, type Grant } from '../api';
import { sfx } from '../audio';
import { go, register } from '../router';
import { act } from '../state';
import { esc, grantsHtml, onAct } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { openModal, toast } from '../ui/overlay';
import { errorText, loadInto } from '../ui/status';
import '../ui/fit';

register('survey', {
  title: 'アンケート',
  nav: 'news',
  mount(el) {
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page survey"><div class="sv-wrap"></div></section></div>`;
    const wrap = el.querySelector<HTMLElement>('.sv-wrap')!;
    let def: SurveyDef | null = null;
    let answers: SurveyAnswers = {};
    let answered = false;

    const complete = () => !!def && def.questions.every((q) => q.optional || (Array.isArray(answers[q.id]) ? (answers[q.id] as number[]).length > 0 : answers[q.id] !== undefined));
    const sync = () => {
      wrap.querySelectorAll<HTMLButtonElement>('.sv-opts .chip').forEach((b) => {
        const v = answers[b.dataset.q!];
        const i = Number(b.dataset.i);
        b.setAttribute('aria-pressed', String(Array.isArray(v) ? v.includes(i) : v === i));
      });
      const send = wrap.querySelector<HTMLButtonElement>('[data-act="send"]');
      if (send) send.disabled = !complete();
    };

    const draw = () => {
      if (!def) {
        wrap.innerHTML = `<div class="state-box empty">${artIcon('bell')}<p>いま受付中のアンケートはありません</p></div>`;
        return;
      }
      wrap.innerHTML = `<header class="screen-head"><h1 class="screen-title">${artIcon('bell')}${esc(def.title)}</h1>${!answered && def.reward.length ? `<div class="end sv-reward">${grantsHtml(def.reward as Grant[])}</div>` : ''}</header>
        <div class="sv-body fit-scroll">${def.questions
          .map((q) =>
            q.kind === 'text'
              ? `<label class="sv-q"><span class="sv-t">${esc(q.text)}</span><textarea data-q="${esc(q.id)}" rows="1" maxlength="${textMax(q)}" placeholder="${esc(q.placeholder ?? '')}">${esc(typeof answers[q.id] === 'string' ? (answers[q.id] as string) : '')}</textarea></label>`
              : `<div class="sv-q" role="group" aria-label="${esc(q.text)}"><span class="sv-t">${esc(q.text)}</span><div class="sv-opts">${q.options
                  .map((o, i) => `<button type="button" class="chip" data-act="pick" data-q="${esc(q.id)}" data-i="${i}" aria-pressed="false">${esc(o)}</button>`)
                  .join('')}</div></div>`,
          )
          .join('')}</div>
        <footer class="sv-foot"><button class="btn primary wide" data-act="send" disabled>${answered ? '答え直す' : '送る'}</button></footer>`;
      sync();
    };

    void loadInto(wrap, 'アンケート', api.survey, (r) => {
      def = r.survey;
      answers = { ...(r.answers ?? {}) };
      answered = !!r.answers;
      draw();
    });

    const onInput = (e: Event) => {
      const t = e.target as HTMLTextAreaElement;
      if (!t.matches('textarea[data-q]')) return;
      answers[t.dataset.q!] = t.value;
    };
    wrap.addEventListener('input', onInput);

    const offAct = onAct(wrap, {
      pick: (b) => {
        const q = def?.questions.find((x) => x.id === b.dataset.q);
        if (!q || q.kind === 'text') return;
        const i = Number(b.dataset.i);
        if (q.kind === 'single') answers[q.id] = i;
        else {
          const cur = Array.isArray(answers[q.id]) ? [...(answers[q.id] as number[])] : [];
          const at = cur.indexOf(i);
          if (at >= 0) cur.splice(at, 1);
          else {
            // 上限なら古い選択を外して入れ替える
            if (cur.length >= multiMax(q)) cur.shift();
            cur.push(i);
          }
          answers[q.id] = cur;
        }
        sync();
      },
      send: async (b) => {
        if (!def || !complete()) return;
        (b as HTMLButtonElement).disabled = true;
        try {
          const r = await act(api.answerSurvey(def.id, answers));
          if (r.granted) {
            sfx('reward');
            openModal(`<div class="sv-thanks"><p>${icon('check')}ご協力ありがとうございました。</p>${grantsHtml(r.granted)}</div>`, { title: 'お礼' });
          } else toast('答え直しました', 'good');
          go('news');
        } catch (e) {
          toast(errorText(e), 'error');
          (b as HTMLButtonElement).disabled = false;
        }
      },
    });
    return () => {
      offAct();
      wrap.removeEventListener('input', onInput);
    };
  },
});
